import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  subscribeToConversationSseFrames,
  type ObservedConversationSseFrame,
} from "../utils/idaChatStreamConfig";

export const TOOL_COMPOSING_TIMEOUT_MS = 90_000;

type CompositionSource = "typed" | "voice";

interface Composition {
  callId: string;
  conversationId: string | null;
  label: string;
  source: CompositionSource;
  startedAt: number;
  timedOut: boolean;
}

function compositionKey(source: CompositionSource, callId: string): string {
  return `${source}:${callId}`;
}

function frameConversationId(
  frame: ObservedConversationSseFrame,
): string | null {
  return frame.conversation_id === undefined
    ? null
    : String(frame.conversation_id);
}

export function getToolComposingLabel(
  compositions: Record<string, Composition>,
): string | null {
  const current = Object.values(compositions).sort(
    (left, right) => left.startedAt - right.startedAt,
  )[0];
  if (!current) return null;
  if (current.timedOut) return "Working";
  if (current.source === "voice") return "your request";
  return current.label;
}

export function useToolComposingFeedback(
  conversationId: string | number | null,
) {
  const [compositions, setCompositions] = useState<Record<string, Composition>>(
    {},
  );
  const timersRef = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  const selectedConversationId =
    conversationId === null ? null : String(conversationId);
  const selectedConversationIdRef = useRef(selectedConversationId);
  selectedConversationIdRef.current = selectedConversationId;

  const clearKey = useCallback((key: string) => {
    const timer = timersRef.current.get(key);
    if (timer) clearTimeout(timer);
    timersRef.current.delete(key);
    setCompositions((current) => {
      if (!(key in current)) return current;
      const next = { ...current };
      delete next[key];
      return next;
    });
  }, []);

  const clearSource = useCallback((source: CompositionSource) => {
    const prefix = `${source}:`;
    timersRef.current.forEach((timer, key) => {
      if (!key.startsWith(prefix)) return;
      clearTimeout(timer);
      timersRef.current.delete(key);
    });
    setCompositions((current) =>
      Object.fromEntries(
        Object.entries(current).filter(
          ([, composition]) => composition.source !== source,
        ),
      ),
    );
  }, []);

  const clearAll = useCallback(() => {
    timersRef.current.forEach((timer) => clearTimeout(timer));
    timersRef.current.clear();
    setCompositions({});
  }, []);

  const start = useCallback(
    (callId: string, label: string, source: CompositionSource) => {
      if (!callId) return;
      const key = compositionKey(source, callId);
      const priorTimer = timersRef.current.get(key);
      if (priorTimer) clearTimeout(priorTimer);
      setCompositions((current) => ({
        ...current,
        [key]: {
          callId,
          conversationId: selectedConversationIdRef.current,
          label,
          source,
          startedAt: Date.now(),
          timedOut: false,
        },
      }));
      timersRef.current.set(
        key,
        setTimeout(() => {
          setCompositions((current) => {
            const composition = current[key];
            if (!composition) return current;
            return {
              ...current,
              [key]: { ...composition, timedOut: true },
            };
          });
          timersRef.current.delete(key);
        }, TOOL_COMPOSING_TIMEOUT_MS),
      );
    },
    [],
  );

  const clearCall = useCallback(
    (callId: string, source: CompositionSource) =>
      clearKey(compositionKey(source, callId)),
    [clearKey],
  );

  const handleFrame = useCallback(
    (frame: ObservedConversationSseFrame) => {
      const eventConversationId = frameConversationId(frame);
      if (
        selectedConversationId === null ||
        eventConversationId === null ||
        eventConversationId !== selectedConversationId
      ) {
        return;
      }

      if (frame.type === "tool_composing") {
        if (frame.call_id) {
          start(frame.call_id, frame.label?.trim() || "Working", "typed");
        }
        return;
      }
      if (frame.type === "assistant_turn_started") {
        clearSource("voice");
        return;
      }
      if (
        (frame.type === "tool_calling" || frame.type === "tool_start") &&
        frame.call_id
      ) {
        clearCall(frame.call_id, "typed");
        return;
      }
      if (
        frame.type === "content_delta" ||
        frame.type === "job_started" ||
        frame.type === "done" ||
        frame.type === "error" ||
        frame.type === "cancelled" ||
        frame.type === "queue_item_failed"
      ) {
        clearAll();
      }
    },
    [clearAll, clearCall, clearSource, selectedConversationId, start],
  );

  useEffect(() => subscribeToConversationSseFrames(handleFrame), [handleFrame]);

  useEffect(() => {
    clearAll();
    return clearAll;
  }, [clearAll, selectedConversationId]);

  const label = useMemo(
    () => getToolComposingLabel(compositions),
    [compositions],
  );
  const startVoiceComposition = useCallback(
    (callId: string) => {
      clearSource("voice");
      start(callId, "your request", "voice");
    },
    [clearSource, start],
  );
  const clearVoiceCompositions = useCallback(
    () => clearSource("voice"),
    [clearSource],
  );

  return {
    label,
    startVoiceComposition,
    clearVoiceCompositions,
  };
}
